<template>
  <v-container fluid style="background: #eaedf1">
    <v-sheet class="pa-3">
      <v-toolbar flat height="80">
        <v-btn
          :disabled="!getPermission('campaigns.create') || store.unpaid_billing"
          color="secondary"
          class="text-capitalize px-5"
          depressed
          v-text="translations.createNewCampaign[lang]"
          @click="startCreateNewCampaign"
        ></v-btn>
        <v-tooltip v-if="getPermission('campaigns.create') && store.unpaid_billing" color="red" top>
          <template v-slot:activator="{ on }">
            <v-icon color="error" class="ml-2" v-on="on">mdi-alert-circle </v-icon>
          </template>
          <span>
            {{ translations.paymentDue[lang] }}
          </span>
        </v-tooltip>
        <v-spacer></v-spacer>

        <v-col cols="12" sm="4" class="pt-0">
          <b-select
            :items="statuses"
            :item-text="`name[${lang}]`"
            item-value="campaign_status_id"
            v-model="status"
            noClearIcon
            :label="translations.status[lang]"
          ></b-select>
        </v-col>
        <v-col cols="12" sm="4" class="pt-3">
          <b-search-field v-model="search"></b-search-field>
        </v-col>
      </v-toolbar>
      <v-data-table
        :headers="headers"
        :loading="loading"
        :items="campaigns"
        :footer-props="{
          itemsPerPageOptions: [12],
          showCurrentPage: true,
          pageText: `${page * 12 - 11}-${
            page * 12 <= serverItemsLength ? page * 12 : serverItemsLength
          } ${translations.paginationText[lang]} ${serverItemsLength}`,
        }"
        :page.sync="page"
        :server-items-length="serverItemsLength"
        class="b-outlined rounded-0"
      >
        <template v-slot:item.title="{ item }">
          <div class="d-flex flex-column campaign-title-cell">
            <div v-if="isCredit" class="d-flex align-center">
              <v-tooltip v-if="getCampaignCostDisplay(item)" color="secondary" top>
                <template v-slot:activator="{ on, attrs }">
                  <v-chip
                    small
                    label
                    :class="[getCampaignEstimatedChipClass(item), 'ml-0']"
                    v-on="on"
                    v-bind="attrs"
                  >
                    {{ getCampaignCostDisplay(item) }}
                  </v-chip>
                </template>
                <span class="font-weight-bold">{{ getCampaignCostLabel(item) }}</span>
              </v-tooltip>
            </div>
            <router-link
              :to="{ name: 'CampaignReport', params: { campaign_id: item.campaign_id } }"
              class="b-signin-link"
            >
              <span class="font-weight-bold px-1">{{ item.title }}</span>
            </router-link>
          </div>
        </template>
        <template v-slot:item.campaign_status="{ item }">
          <span
            class="font-weight-bold px-1"
            v-text="item.campaign_status.campaign_status_name[lang]"
          ></span>
        </template>
        <template v-slot:item.activated_at="{ item }">
          <span class="font-weight-bold px-1">{{ localizeDate(item.activated_at, true) }}</span>
        </template>
        <template v-slot:item.system_notification_ids="{ item }">
          <div class="d-flex">
            <span
              v-for="(notification, index) in filterCampaignChannels(item.system_notification_ids)"
              :key="index"
              class="font-weight-bold px-1 d-flex align-center"
            >
              <v-icon
                v-if="index && item.notifier_type && item.notifier_type == 'priority'"
                size="18"
                class="ml-n2 mr-1"
                v-text="icons.mdiArrowRight"
                color="secondary"
              ></v-icon>
              <Icon
                v-if="notification.channel == 'viber'"
                icon="simple-icons:viber"
                color="secondary"
                :style="{
                  fontSize: '20px',
                }"
              />
              <v-icon
                v-else
                v-text="notificationIcons[notification.channel]"
                color="secondary"
              ></v-icon>
            </span>
          </div>
        </template>
        <template v-slot:item.created_at="{ item }">
          <span class="font-weight-bold px-1">{{ localizeDate(item.created_at, true) }}</span>
        </template>
        <template v-slot:item.report_url="{ item }">
          <a
            v-if="item.report_url && item.report_url !== '-'"
            :href="item.report_url"
            target="_blank"
            class="b-signin-link"
          >
            <v-icon small class="mr-1" v-text="icons.mdiOpenInNew"></v-icon>
            <span class="font-weight-bold">{{ item.report_url }}</span>
          </a>
          <span v-else>-</span>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-col v-if="!getCampaignActions(item).length"> - </v-col>
          <v-tooltip
            v-for="(action, index) in getCampaignActions(item)"
            :key="index"
            color="secondary"
            top
          >
            <template v-slot:activator="{ on }">
              <v-btn
                :disabled="!getPermission('campaigns.update')"
                class="mr-1"
                icon
                v-on="on"
                :color="action.theme"
                @click="action.doAction"
              >
                <v-icon v-text="action.icon"></v-icon>
              </v-btn>
            </template>

            <span class="font-weight-bold">{{ action.name[lang] }}</span>
          </v-tooltip>
        </template>
      </v-data-table>

      <v-dialog v-model="statusDialogData.open" max-width="500">
        <b-card
          :title="statusDialogData.title"
          @cancel="closeStatusDialog()"
          @submit="statusDialogData.action"
          :loading="loading"
          :theme="statusDialogData.theme"
        >
          <v-col cols="12" md="12" class="d-flex flex-column pa-0">
            <span class="text-subtitle-1">{{ statusDialogData.description }}:</span>
            <v-col class="d-flex align-center">
              <span class="text-h6 font-weight-regular"
                >#{{ statusDialogData.campaign.campaign_id }}</span
              >
              <v-divider class="ml-4 mr-4" vertical></v-divider>
              <span class="text-h5">{{ statusDialogData.campaign.title }}</span>
            </v-col>
          </v-col>
        </b-card>
      </v-dialog>

      <v-dialog v-model="additionalDialog" max-width="500">
        <AdditionalInfo @cancel="additionalDialog = false" />
      </v-dialog>
    </v-sheet>
  </v-container>
</template>

<script>
import {
  mdiCalendarMonth,
  mdiChat,
  mdiBell,
  mdiEmail,
  mdiLock,
  mdiEye,
  mdiCached,
  mdiEmailOutline,
  mdiMessageReplyTextOutline,
  mdiMessageTextOutline,
  mdiBellOutline,
  mdiArrowRight,
  mdiCancel,
  mdiPlay,
  mdiRunFast,
  mdiOpenInNew,
} from '@mdi/js';
import { mapMutations, mapState, mapActions } from 'vuex';

import AdditionalInfo from '@/components/loyaltyPanel/viewCampaigns/AdditionalInfo';
import translations from '@/utils/translations/loyaltyPanel/campaigns/viewCampaigns';
import debounce from 'lodash/debounce';
import { Icon } from '@iconify/vue2';

export default {
  name: 'ViewCampaigns',

  mixins: [translations],

  components: {
    AdditionalInfo,
    Icon,
  },

  data() {
    return {
      icons: {
        mdiCalendarMonth,
        mdiChat,
        mdiBell,
        mdiEmail,
        mdiLock,
        mdiEye,
        mdiCached,
        mdiEmailOutline,
        mdiMessageReplyTextOutline,
        mdiMessageTextOutline,
        mdiBellOutline,
        mdiArrowRight,
        mdiCancel,
        mdiPlay,
        mdiRunFast,
        mdiOpenInNew,
      },
      itemsPerPageOptions: [10, 20, 30, -1],
      page: +this.$route.query.page,
      // changingStatusCampaignId: null,
      dialogStatusSystem: null,
      statusDialogData: {
        open: false,
        title: '',
        description: '',
        campaign: {},
        theme: '',
        action: () => {},
      },

      newStatus: null,
      additionalDialog: false,
      search: +this.$route.query.q || '',
      status: +this.$route.query.status || null,
      notificationIcons: {
        sms: mdiMessageTextOutline,
        mail: mdiEmailOutline,
        push: mdiBellOutline,
        viber: 'simple-icons:viber',
      },
      filterStatuses: [
        {
          value: 0,
          title: 'All',
        },
        {
          value: 1,
          title: 'Pending',
        },
        {
          value: 2,
          title: 'Running',
        },
        {
          value: 3,
          title: 'Completed',
        },
      ],
    };
  },

  computed: {
    ...mapState(['loading', 'errorMessage', 'serverItemsLength', 'isSuperAdmin', 'store']),
    ...mapState('loyaltyPanel/campaigns/viewCampaigns', [
      'campaigns',
      'campaignStatuses',
      'systemNotifications',
    ]),

    lang() {
      return this.$route.params.lang;
    },
    locale() {
      if (this.lang == 'el') return 'el-GR';
      if (this.lang == 'it') return 'it-IT';
      return 'en-GB';
    },

    // channels() {
    //     return this.systemNotifications.map( e => ({
    //         ...e,
    //         icon:
    //     }) )
    // },

    statuses() {
      return [
        {
          campaign_status_id: null,
          name: {
            el: 'Όλα',
            en: 'All',
            it: 'Tutti',
          },
          system: 'all',
        },
        ...this.campaignStatuses,
      ];
    },

    headers() {
      let headers = [
        {
          text: this.translations.campaignId[this.lang],
          value: 'campaign_id',
          width: 200,
        },
        {
          text: this.translations.title[this.lang],
          value: `title`,
          width: 250,
        },
        {
          text: this.translations.status[this.lang],
          value: 'campaign_status',
          width: 200,
        },
        {
          text: this.translations.startDate[this.lang],
          value: `activated_at`,
          width: 200,
        },
        {
          text: this.translations.notifications[this.lang],
          value: 'system_notification_ids',
          width: 200,
        },
        {
          text: this.translations.createdAt[this.lang],
          value: 'created_at',
          width: 200,
        },
        {
          text: this.translations.reportUrl[this.lang],
          value: 'report_url',
          width: 200,
        },
        {
          text: this.translations.actions[this.lang],
          value: 'actions',
        },
        // {
        //     text: this.translations.link[this.lang],
        //     value: "link",
        //     width: 150,
        // },
      ];
      return headers;
    },

    query() {
      let query = '?';
      for (let key in this.$route.query) {
        if (this.$route.query[key]) {
          query += `${key}=${this.$route.query[key]}&`;
        }
      }
      return query.slice(0, query.length - 1);
    },
    isCredit() {
      return Boolean(this.$store.state.store?.is_credit);
    },
  },

  watch: {
    query(val) {
      if (!this.page) {
        this.page = 1;
      }
      console.log('search this.query:', this.query);

      this.getCampaigns({
        query: val,
      });
    },

    page(page) {
      this.$router.push({ query: { ...this.$route.query, page } });
    },

    search(val) {
      this.page = 1;
      this.$router.push({ query: { ...this.$route.query, q: val || undefined } });
    },

    status(val) {
      this.page = 1;
      this.$router.push({ query: { ...this.$route.query, status: val || undefined } });
    },
  },

  methods: {
    ...mapActions('loyaltyPanel/campaigns/viewCampaigns', [
      'getCampaigns',
      'changeCampaignStatus',
      'getSingleCampaign',
      'getCampaignStatuses',
      'getCampaignSystemNotifications',
    ]),
    ...mapMutations('loyaltyPanel/campaigns/newCampaign', [
      'setNewCampaign',
      'setStep',
      'setCanGoToNextStep',
      'setUploadingImage',
    ]),
    ...mapActions(['getPermission']),
    openStatusDialog(item, newStatusSystem, theme) {
      this.statusDialogData = {
        open: true,
        title: this.translations[`${newStatusSystem}CampaignTitle`][this.lang] || '',
        description: this.translations[`${newStatusSystem}CampaignText`][this.lang] || '',
        campaign: item,
        theme,
        action: () => {
          this.changeStatus(item, newStatusSystem);
        },
      };
    },

    closeStatusDialog() {
      this.statusDialogData = {
        open: false,
        title: '',
        description: '',
        campaign: {},
        theme: '',
        action: () => {},
      };
    },
    localizeDate(date, short) {
      return date
        ? new Date(date).toLocaleString(this.locale, {
            weekday: short ? undefined : 'long',
            year: 'numeric',
            month: short ? 'numeric' : 'long',
            day: 'numeric',
          })
        : '-';
    },
    async changeStatus(item, statusSystem) {
      let activateStatus = this.statuses.find((x) => x.system == statusSystem);
      let newStatus = await this.changeCampaignStatus({
        campaign_id: item.campaign_id,
        body: {
          campaign_status_id: activateStatus.campaign_status_id,
        },
      });
      if (newStatus) {
        item.campaign_status_id = activateStatus.campaign_status_id;
        this.getCampaigns({
          query: this.query,
        });
      }
      this.closeStatusDialog();
    },

    async activateCampaign() {
      let body = {
        campaign_status_id: this.newStatus,
      };

      let data = {
        campaign_id: this.changingStatusCampaignId,
        body: body,
      };
      await this.changeCampaignStatus(data);
      this.closeStatusDialog();
      this.getCampaigns({
        query: this.query,
      });
    },

    async openAdditionalInfo(id) {
      await this.getSingleCampaign(id);
      this.additionalDialog = true;
    },

    filterCampaignChannels(ids) {
      return this.systemNotifications.filter((e) => ids.includes(e.system_notification_id));
    },

    handleSearch() {
      if (this.page != 1) {
        this.page = 1;
      } else {
        this.$router.push({ query: { ...this.$route.query, q: this.search } });
      }
    },

    getCampaignCostDisplay(item) {
      const { val } = this.getCampaignCostInfo(item);
      return this.formatCampaignCost(val);
    },

    getCampaignEstimatedColor(item) {
      const { val } = this.getCampaignCostInfo(item);
      if (val === null || val === undefined) return 'secondary';
      const est = Number(val);
      if (Number.isNaN(est)) return 'secondary';
      const credits = Number(this.$store.state.loyaltyPanel?.company?.credits ?? 0);
      if (Number.isNaN(credits)) return 'secondary';
      return credits >= est ? 'green' : 'error';
    },

    getCampaignEstimatedChipClass(item) {
      const { val } = this.getCampaignCostInfo(item);
      const defaultClass = 'billing-type-chip blue-grey lighten-5 blue-grey--text text--darken-2';
      if (val === null || val === undefined) return defaultClass;
      const est = Number(val);
      if (Number.isNaN(est)) return defaultClass;
      const statusObj2 =
        this.statuses.find(
          (x) => x.campaign_status_id == item.campaign_status.campaign_status_id
        ) || {};
      if (['completed', 'canceled'].includes(statusObj2.system)) {
        return 'billing-type-chip grey lighten-4 grey--text text--darken-2';
      }
      const credits = Number(this.$store.state.loyaltyPanel?.company?.credits ?? 0);
      if (Number.isNaN(credits)) return defaultClass;
      return credits >= est
        ? 'billing-type-chip green lighten-5 green--text text--darken-3'
        : 'billing-type-chip red lighten-5 red--text text--darken-3';
    },

    getCampaignCostLabel(item) {
      const { isActual } = this.getCampaignCostInfo(item);
      return isActual
        ? this.translations.actualCost[this.lang]
        : this.translations.estimatedCost[this.lang];
    },

    getCampaignCostInfo(item) {
      if (!item) return { val: null, isActual: false, statusObj: {} };

      const statusObj = item.campaign_status || {};
      const actual = item.data?.actual_cost;
      const estimate = item.data?.cost_estimate ?? null;

      const statusId = statusObj.campaign_status_id ?? item.campaign_status_id ?? null;
      let resolvedStatus = statusObj;
      if (statusId !== null && Array.isArray(this.statuses)) {
        const found = this.statuses.find((s) => s.campaign_status_id == statusId);
        if (found) {
          resolvedStatus = found;
        }
      }

      const isActual =
        actual !== null && actual !== undefined && resolvedStatus.system === 'completed';
      const val = isActual ? actual : estimate;
      return { val, isActual, statusObj: resolvedStatus };
    },

    formatCampaignCost(val) {
      if (val === null || val === undefined) return null;
      const num = Number(val);
      if (Number.isNaN(num)) return `${val} €`;
      const formatted = Number.isInteger(num) ? num.toString() : num.toFixed(2).replace('.', ',');
      return `${formatted} €`;
    },

    getCampaignActions(item) {
      let actions = [];
      let itemStatus =
        this.statuses.find(
          (x) => x.campaign_status_id == item.campaign_status.campaign_status_id
        ) || {};
      if (itemStatus.system == 'pending') {
        actions.push({
          name: {
            el: 'Ενεργοποίηση',
            en: 'Activate',
            it: 'Attivare',
          },
          icon: this.icons.mdiPlay,
          theme: 'green',
          doAction: () => {
            this.openStatusDialog(item, 'activated', 'green');
          },
        });
      }
      if (itemStatus.system == 'user_confirmation') {
        actions.push({
          name: {
            el: 'Τρέξιμο',
            en: 'Run',
            it: 'Correre',
          },
          icon: this.icons.mdiRunFast,
          theme: 'green',
          doAction: () => {
            this.openStatusDialog(item, 'running', 'green');
          },
        });
      }
      if (!['completed', 'canceled'].includes(itemStatus.system)) {
        // let cancelStatus = this.statuses.find(x => x.system == 'canceled')
        actions.push({
          name: {
            el: 'Ακύρωση',
            en: 'Cancel',
            it: 'Annullamento',
          },
          icon: this.icons.mdiCancel,
          theme: 'red',
          doAction: () => {
            this.openStatusDialog(item, 'canceled', 'red');
          },
        });
      }

      return actions;
    },
    startCreateNewCampaign() {
      this.setNewCampaign({});
      this.setStep(1);
      this.setCanGoToNextStep(false);
      this.setUploadingImage(null);
      this.$router.push(`/${this.lang}/loyaltyPanel/campaigns/new-campaign`);
    },
  },

  mounted() {
    this.getCampaigns({
      query: this.query,
    });
    this.getCampaignStatuses();
    this.getCampaignSystemNotifications();
  },

  created() {
    this.debouncedSearch = debounce(this.handleSearch, 500);
    this.search = this.$route.query.q || '';
  },

  beforeCreate() {
    if (!this.$route.query.page) {
      this.$router.replace({
        query: {
          page: 1,
          ...this.$route.query,
        },
      });
    }
  },
};
</script>

<style scoped>
.b-outlined {
  border: 1px solid rgba(0, 0, 0, 0.12);
}

.campaign-title-cell {
  padding-top: 8px;
  padding-bottom: 8px;
}

.billing-type-chip {
  padding: 4px 8px;
  border-radius: 14px;
  font-size: 13px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
}
</style>
